import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ProductHistoryListResponseDto } from '../../../api/models/ProductHistoryListResponseDto';
import { formatHistoryDate, getEventTypeLabel } from '../../../utils/productHistory.utils';
import { generateHistoryMessage } from '../../../utils/productHistoryMessageGenerator';
import { searchHistoryByRelatedEntity } from '../../../utils/productMovementHistorySearch';

export interface RequestHistorySectionProps {
  requestId: number;
}

const RequestHistorySection: React.FC<RequestHistorySectionProps> = ({ requestId }) => {
  const [entries, setEntries] = useState<ProductHistoryListResponseDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await searchHistoryByRelatedEntity({
          relatedEntityType: 'INVENTORY_REQUEST',
          relatedEntityId: requestId,
        });
        const list = response?.data ?? [];
        if (!cancelled) {
          setEntries(list as ProductHistoryListResponseDto[]);
        }
      } catch (err) {
        console.error('Error loading request history', err);
        if (!cancelled) {
          setError('No se pudo cargar el historial.');
          setEntries([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  }

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Sin eventos registrados.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {entries.map((entry) => {
        const performedBy = entry.performedBy
          ? `${entry.performedBy.firstName ?? ''} ${entry.performedBy.lastName ?? ''}`.trim()
          : '—';
        const message =
          generateHistoryMessage(entry) ??
          (typeof entry.transferCode === 'string' ? entry.transferCode : undefined);

        return (
          <Box
            key={entry.id}
            sx={(theme) => ({
              borderLeft: `3px solid ${theme.palette.info.main}`,
              pl: 1.5,
              py: 0.5,
            })}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              {formatHistoryDate(entry.performedAt)} · {performedBy}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {getEventTypeLabel(entry.eventType, entry.relatedEntityType)}
            </Typography>
            {message && (
              <Typography variant="body2" color="text.secondary">
                {message}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default RequestHistorySection;
